import { UserContext } from "context/user";
import { gql, useMutation, useQuery } from "@apollo/client";
import { useContext, useEffect, useRef, useState } from "react";

// Components
import { Card, Chip, Grid, InputLabel, Tooltip } from "@mui/material";
import Footer from "components/Footer";
import DashboardLayout from "components/LayoutContainers/DashboardLayout";
import DashboardNavbar from "components/Navbars/DashboardNavbar";
import SoftBox from "components/SoftBox";
import SoftTypography from "components/SoftTypography";

import { MessageManager } from "context";
import SoftInput from "components/SoftInput";
import SoftButton from "components/SoftButton";
import {
  DECLARACION_TEMPLATE_VARIABLES,
  DEFAULT_DECLARACION_TEMPLATE,
} from "utils/declaracionTemplate";

const EMPTY_DATOS = {
  texto: "",
};

function DatosDeclaracion() {
  const { verificarAcceso } = useContext(UserContext);
  const [datos, setDatos] = useState(EMPTY_DATOS);
  const { handleSnackbar } = useContext(MessageManager);
  const textoInputRef = useRef(null);

  const [saveDatos, { loading: loadingSave }] = useMutation(
    gql`
      mutation saveDatos($input: DatosDeclaracionInput!) {
        saveDatos(input: $input) {
          id
        }
      }
    `
  );
  const { data } = useQuery(
    gql`
      query getDatos {
        datos {
          __typename @skip(if: true)
          texto
        }
      }
    `,
    {
      fetchPolicy: "no-cache",
    }
  );

  const handleChangeDatos = (event) => {
    const { name, value } = event.target;
    setDatos((prev) => ({ ...(prev || EMPTY_DATOS), [name]: value }));
  };

  const handleInsertVariable = (token) => {
    const input = textoInputRef.current;

    setDatos((prev) => {
      const currentTexto = prev?.texto || "";
      const selectionStart = input?.selectionStart ?? currentTexto.length;
      const selectionEnd = input?.selectionEnd ?? currentTexto.length;
      const nextTexto = `${currentTexto.slice(0, selectionStart)}${token}${currentTexto.slice(
        selectionEnd
      )}`;

      requestAnimationFrame(() => {
        if (!input) return;

        const nextCursorPosition = selectionStart + token.length;
        input.focus();
        input.setSelectionRange(nextCursorPosition, nextCursorPosition);
      });

      return { ...(prev || EMPTY_DATOS), texto: nextTexto };
    });
  };

  const handleSetSuggestedText = () => {
    setDatos((prev) => ({ ...(prev || EMPTY_DATOS), texto: DEFAULT_DECLARACION_TEMPLATE }));

    requestAnimationFrame(() => {
      textoInputRef.current?.focus();
    });
  };

  const handleSaveDatos = async () => {
    try {
      await saveDatos({
        variables: {
          input: datos,
        },
      });
      handleSnackbar("Guardado correctamente", "success");
    } catch (e) {
      handleSnackbar(e.message || "Error al guardar", "error");
    }
  };
  useEffect(() => {
    if (data?.datos) {
      setDatos({ ...EMPTY_DATOS, ...data.datos });
    }
  }, [data]);

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <SoftBox py={3}>
        <SoftBox>
          <Card>
            <Grid container spacing={2} p={3}>
              <Grid item xs={12}>
                <SoftBox display="flex" justifyContent="space-between" alignItems="center">
                  <SoftTypography variant="h5" fontWeight="bold">
                    Datos generales para Declaraciones Juradas.
                  </SoftTypography>
                </SoftBox>
              </Grid>
              <Grid item xs={12}>
                <InputLabel htmlFor="texto">
                  <SoftTypography component="label" variant="caption" fontWeight="bold">
                    Texto final del PDF
                  </SoftTypography>
                </InputLabel>
                <SoftInput
                  id="texto"
                  placeholder="Escriba el texto completo que se mostrará en el PDF"
                  value={datos?.texto || ""}
                  fullWidth
                  name="texto"
                  onChange={handleChangeDatos}
                  readOnly={!verificarAcceso(252)}
                  multiline
                  rows={6}
                  inputRef={textoInputRef}
                />
              </Grid>
              <Grid item xs={12}>
                <SoftBox
                  display="flex"
                  justifyContent="space-between"
                  alignItems={{ xs: "flex-start", md: "center" }}
                  gap={1}
                  flexDirection={{ xs: "column", md: "row" }}
                >
                  <SoftTypography variant="caption" color="text" fontWeight="regular">
                    Escriba el texto completo y haga clic en una variable para insertarla en la
                    posición actual del cursor.
                  </SoftTypography>
                  <SoftButton
                    variant="outlined"
                    color="dark"
                    size="small"
                    onClick={handleSetSuggestedText}
                  >
                    Usar texto sugerido
                  </SoftButton>
                </SoftBox>
                <SoftBox mt={1.5} display="flex" flexWrap="wrap" gap={1}>
                  {DECLARACION_TEMPLATE_VARIABLES.map((variable) => (
                    <Tooltip key={variable.key} title={variable.description} placement="top">
                      <Chip
                        label={variable.token}
                        variant="outlined"
                        size="small"
                        clickable
                        onClick={() => handleInsertVariable(variable.token)}
                      />
                    </Tooltip>
                  ))}
                </SoftBox>
              </Grid>
            </Grid>
          </Card>
          {/* Guardar */}
          <Grid display={"flex"} justifyContent="flex-end" py={2}>
            <SoftButton
              variant="gradient"
              color="primary"
              onClick={handleSaveDatos}
              disabled={loadingSave || !datos || !verificarAcceso(252)}
            >
              Guardar cambios
            </SoftButton>
          </Grid>
        </SoftBox>
      </SoftBox>
      <Footer />
    </DashboardLayout>
  );
}

export default DatosDeclaracion;
